/**
 * useOwnerChat
 *
 * TanStack Query hook for Help Genie chat.
 * Provides optimistic message updates, session management, streaming,
 * retry on failure, and feedback submission.
 */

import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { OwnerChatService } from "@/services/brain/OwnerChatService";
import { ImpersonationService } from "@/services/users/ImpersonationService";

import type { BrainChatMessage, BrainScopeContext } from "@/types/brain";

const MESSAGES_KEY = (sessionId: string) => ["brain-chat-messages", sessionId];
const SESSIONS_KEY = (impersonatedUserId?: string) =>
  impersonatedUserId ? ["brain-chat-sessions", impersonatedUserId] : ["brain-chat-sessions"];

export const useOwnerChat = (sessionId: string | null, enabled = false) => {
  const queryClient = useQueryClient();
  const [streamingText, setStreamingText] = useState("");
  const [streamingStatus, setStreamingStatus] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingToolResults, setStreamingToolResults] = useState<Array<{ tool: string; summary: string }>>([]);
  const abortRef = useRef(false);
  const streamingTextRef = useRef("");
  const streamingStatusRef = useRef<string | null>(null);
  const boundaryPendingRef = useRef(false);
  const lastFailedMessageRef = useRef<string | null>(null);
  const impersonatedUserId = ImpersonationService.getImpersonatedUserId();

  // ── Messages ─────────────────────────────────────────────────────────────

  const messagesQuery = useQuery({
    queryKey: MESSAGES_KEY(sessionId ?? ""),
    queryFn: () => OwnerChatService.getMessages(sessionId!),
    enabled: !!sessionId,
    // Keep cached data fresh so an optimistic user message added via
    // setQueryData (in sendMessage) isn't clobbered by a background
    // refetch on mount. onDone still calls invalidateQueries to force a
    // refresh once the server has persisted both messages.
    staleTime: 60_000,
  });

  // ── Sessions ─────────────────────────────────────────────────────────────

  const sessionsQuery = useQuery({
    queryKey: SESSIONS_KEY(impersonatedUserId),
    queryFn: () => OwnerChatService.getSessions(),
    staleTime: 30_000,
    enabled,
  });

  // ── Send Message (Streaming) ────────────────────────────────────────────

  const sendMessage = useCallback(
    async (params: {
      message: string;
      sessionOverride?: string;
      scope?: BrainScopeContext | null;
      diagnostic?: boolean;
    }) => {
      const sid = params.sessionOverride ?? sessionId;
      if (!sid) throw new Error("No session");

      // Clear any previous failure
      lastFailedMessageRef.current = null;

      // Optimistic: add user message immediately
      const optimisticMessage: BrainChatMessage = {
        id: `temp-${Date.now()}`,
        user_id: "",
        session_id: sid,
        role: "user",
        content: params.message,
        tool_calls: null,
        tool_results: null,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<BrainChatMessage[]>(MESSAGES_KEY(sid), (old) => [...(old ?? []), optimisticMessage]);

      // Start streaming
      setStreamingText("");
      streamingTextRef.current = "";
      setStreamingStatus(null);
      streamingStatusRef.current = null;
      boundaryPendingRef.current = false;
      setStreamingToolResults([]);
      setIsStreaming(true);
      abortRef.current = false;

      try {
        await OwnerChatService.sendMessageStream(
          {
            message: params.message,
            session_id: sid,
            scope: params.scope ?? null,
            diagnostic: params.diagnostic,
          },
          {
            onDelta: (text) => {
              if (abortRef.current) return;
              // A status/tool_call event marks a new iteration boundary — but
              // only the FIRST delta after that boundary should reset the
              // previous iteration's text; boundaryPendingRef distinguishes
              // "just crossed a boundary" from "still mid-block", which
              // streamingTextRef truthiness alone can't (it's also empty
              // before the very first delta of the whole stream, so that
              // first delta must NOT consume the boundary — otherwise the
              // second delta of the same block wrongly resets and drops
              // the first chunk's text, since the status was never cleared).
              if (boundaryPendingRef.current) {
                boundaryPendingRef.current = false;
                streamingTextRef.current = "";
                setStreamingText("");
                streamingStatusRef.current = null;
                setStreamingStatus(null);
              }
              streamingTextRef.current += text;
              setStreamingText((prev) => prev + text);
            },
            onToolCall: (tool) => {
              boundaryPendingRef.current = true;
              const status = `${tool}...`;
              streamingStatusRef.current = status;
              setStreamingStatus(status);
            },
            onStatus: (text) => {
              boundaryPendingRef.current = true;
              streamingStatusRef.current = text;
              setStreamingStatus(text);
            },
            onToolResult: (tool, summary) => {
              if (abortRef.current) return;
              setStreamingToolResults((prev) => [...prev, { tool, summary }]);
            },
            onDone: (toolCallsMade) => {
              // Optimistically inject the assistant reply into cache so
              // there's no gap between the streaming UI clearing and
              // the refetch completing (prevents flicker).
              if (streamingTextRef.current) {
                queryClient.setQueryData<BrainChatMessage[]>(MESSAGES_KEY(sid), (old) => [
                  ...(old ?? []),
                  {
                    id: `temp-assistant-${Date.now()}`,
                    user_id: "",
                    session_id: sid,
                    role: "assistant" as const,
                    content: streamingTextRef.current,
                    tool_calls: null,
                    tool_results: null,
                    created_at: new Date().toISOString(),
                  },
                ]);
              }
              // Refetch to replace optimistic message with real persisted one
              queryClient.invalidateQueries({ queryKey: MESSAGES_KEY(sid) });
              queryClient.invalidateQueries({ queryKey: SESSIONS_KEY(impersonatedUserId) });

              // Invalidate related queries if the brain performed mutations
              if (toolCallsMade && toolCallsMade.length > 0) {
                // Mutating actions that should trigger cache invalidation
                const mutatingActions = new Set(["create", "update", "delete", "attach", "detach"]);

                // Map resources to their TanStack query keys
                const resourceToQueryKeys: Record<string, string[]> = {
                  genies: ["agent", "agents", "user-agents", "all-agents"],
                  "knowledge-base": ["documents", "documents-infinite", "knowledge-base-docs"],
                  "agent-pages": ["agent-page", "agent", "agents", "user-agents"],
                  insights: ["agent", "agents", "user-agents", "all-agents"],
                };

                const keysToInvalidate = new Set<string>();

                for (const tc of toolCallsMade) {
                  const resource = (tc as any).resource;
                  const action = (tc as any).action;

                  // Only process mutating operations
                  if (resource && action && mutatingActions.has(action)) {
                    const queryKeys = resourceToQueryKeys[resource];
                    if (queryKeys) {
                      queryKeys.forEach((key) => keysToInvalidate.add(key));
                    }

                    // For attach/detach actions, also invalidate genies queries
                    if ((action === "attach" || action === "detach") && resource === "knowledge-base") {
                      keysToInvalidate.add("agents");
                      keysToInvalidate.add("user-agents");
                      keysToInvalidate.add("all-agents");
                    }
                  }
                }

                // Invalidate all collected query keys
                keysToInvalidate.forEach((key) => {
                  queryClient.invalidateQueries({ queryKey: [key] });
                });
              }
            },
            onError: () => {
              lastFailedMessageRef.current = params.message;
              toast.error("Your Genie hit a bump — try again in a moment.");
            },
          },
        );
      } catch (error) {
        console.error("Owner chat send error:", error);
        lastFailedMessageRef.current = params.message;
        toast.error("Couldn't send your message right now. Check your connection and try again.");
        // Rollback optimistic update
        queryClient.setQueryData<BrainChatMessage[]>(MESSAGES_KEY(sid), (old) =>
          (old ?? []).filter((m) => m.id !== optimisticMessage.id),
        );
      } finally {
        setIsStreaming(false);
        setStreamingText("");
        setStreamingStatus(null);
        setStreamingToolResults([]);
      }
    },
    [sessionId, queryClient],
  );

  // ── Retry Last Failed Message ─────────────────────────────────────────

  const retryLastMessage = useCallback(() => {
    const msg = lastFailedMessageRef.current;
    if (!msg) return;
    lastFailedMessageRef.current = null;
    sendMessage({ message: msg });
  }, [sendMessage]);

  // ── Submit Feedback ───────────────────────────────────────────────────

  const submitFeedback = useCallback(
    async (messageId: string, rating: "positive" | "negative", text?: string) => {
      const sid = sessionId;
      if (!sid) return;

      // Optimistic update
      queryClient.setQueryData<BrainChatMessage[]>(MESSAGES_KEY(sid), (old) =>
        (old ?? []).map((m) =>
          m.id === messageId ? { ...m, feedback: { rating, text, created_at: new Date().toISOString() } } : m,
        ),
      );

      try {
        await OwnerChatService.submitFeedback(messageId, { rating, text });
      } catch {
        // Revert on failure
        queryClient.setQueryData<BrainChatMessage[]>(MESSAGES_KEY(sid), (old) =>
          (old ?? []).map((m) => (m.id === messageId ? { ...m, feedback: null } : m)),
        );
      }
    },
    [sessionId, queryClient],
  );

  // ── Create Session ───────────────────────────────────────────────────────

  const createSessionMutation = useMutation({
    mutationFn: () => OwnerChatService.createSession(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY(impersonatedUserId) });
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: (sid: string) => OwnerChatService.deleteSession(sid),
    onSuccess: (_, sid) => {
      queryClient.removeQueries({ queryKey: MESSAGES_KEY(sid) });
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY(impersonatedUserId) });
    },
    onError: () => {
      toast.error("Failed to delete conversation");
    },
  });

  // ── Public API ───────────────────────────────────────────────────────────

  return {
    messages: messagesQuery.data ?? [],
    isLoadingMessages: messagesQuery.isLoading,
    sessions: sessionsQuery.data ?? [],
    isLoadingSessions: sessionsQuery.isLoading,
    sendMessage,
    isSending: isStreaming,
    streamingText,
    streamingStatus,
    streamingToolResults,
    createSession: createSessionMutation.mutateAsync,
    isCreatingSession: createSessionMutation.isPending,
    deleteSession: deleteSessionMutation.mutateAsync,
    isDeletingSession: deleteSessionMutation.isPending,
    retryLastMessage,
    lastFailedMessage: lastFailedMessageRef.current,
    submitFeedback,
  };
};
